import { type EstimatedStatusEnum, RoleEnum, type User, type VideoSortBy } from 'js/src/client';
import {
    getActiveWorkspaceMembers,
    selectActiveWorkspace,
    selectUsersWithAccessToWorkspaceAndProject,
} from 'js/src/data/account';
import {
    NON_PROJECT_VIEW_IDS,
    VIDEOS_KEY,
    viewIdIsProject,
    viewIdIsProjectOrAllVideos,
} from 'js/src/data/dashboard/dashboard-types';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import { type DeepReadonly, roleCanCreateVideos, withStableEmptyArray } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

/* SELECTORS  */
export function selectActiveView(state: RootState) {
    return state.dashboard.activeView;
}

export function selectActiveViewIsProject(state: RootState) {
    const activeView = selectActiveView(state);
    return viewIdIsProject(activeView);
}

export function selectActiveViewIsProjecOrAllVideos(state: RootState) {
    const activeView = selectActiveView(state);
    return viewIdIsProjectOrAllVideos(activeView);
}

export function selectViewData(state: RootState, viewId: number) {
    const viewOrm = new PlainJsStoreORM(state.dashboard.views);
    return viewOrm.getById(viewId);
}

export function viewIsLoaded(state: RootState, viewId: number) {
    return !!selectViewData(state, viewId);
}

/**
 * A project view is "available" only if it's loaded AND belongs to the active
 * workspace. Admins receive projects from every workspace they're a member of,
 * so a project from another workspace can load into the store as a view; it
 * must not be treated as resolvable while a different workspace is active.
 *
 * Compares against `SERVER.USER.workspace_id` rather than the workspaces ORM so
 * the check is correct even before the workspaces list has loaded.
 */
export function projectViewIsInActiveWorkspace(state: RootState, viewId: number): boolean {
    const viewData = selectViewData(state, viewId);
    if (!viewData) return false;
    return viewData.workspaceId === SERVER?.USER?.workspace_id;
}

export function selectViewIds(state: RootState): readonly number[] {
    return withStableEmptyArray(state.dashboard.views.allIds);
}

export function selectActiveViewData(state: RootState) {
    const activeView = selectActiveView(state);
    const view = selectViewData(state, activeView);
    if (!view) throw new Error(`View with id ${activeView} not found`);
    return view;
}

export function selectViewName(state: RootState, viewId: number) {
    const viewData = selectViewData(state, viewId);
    if (!viewData) throw new Error(`View with id ${viewId} not found`);
    return viewData.name;
}

export function selectActiveViewSubTitle(state: RootState) {
    const viewData = selectActiveViewData(state);
    return viewData.subTitle;
}

export function selectActiveViewName(state: RootState) {
    const activeView = selectActiveView(state);
    return selectViewName(state, activeView);
}

export function selectViewIcon(state: RootState, viewId: number) {
    const viewData = selectViewData(state, viewId);
    if (!viewData) throw new Error(`View with id ${viewId} not found`);
    return viewData.icon;
}

export function selectActiveViewIcon(state: RootState) {
    const activeView = selectActiveView(state);
    return selectViewIcon(state, activeView);
}

export function selectViewColor(state: RootState, viewId: number) {
    const viewData = selectViewData(state, viewId);
    if (!viewData) throw new Error(`View with id ${viewId} not found`);
    return viewData.color;
}

export function selectActiveViewColor(state: RootState) {
    const activeView = selectActiveView(state);
    return selectViewColor(state, activeView);
}

const EMPTY_LIST: readonly DeepReadonly<User>[] = [];
export function selectActiveViewMembers(state: RootState): readonly DeepReadonly<User>[] {
    const activeView = selectActiveView(state);
    if (NON_PROJECT_VIEW_IDS.includes(activeView)) {
        return getActiveWorkspaceMembers(state);
    } else {
        // activeView is a project!
        const activeWorkspace = selectActiveWorkspace(state);
        if (!activeWorkspace) {
            return EMPTY_LIST;
        }
        return selectUsersWithAccessToWorkspaceAndProject(state, activeWorkspace.id, activeView);
    }
}

export function selectAllVideoIds(state: RootState): readonly number[] {
    const viewData = selectActiveViewData(state);
    return withStableEmptyArray(new PlainJsStoreORM(viewData[VIDEOS_KEY]).allIds);
}

export function selectVideoDurationById(state: RootState, videoId: number): number | null {
    const video = selectVideoById(state, videoId);
    return video?.duration_seconds || null;
}

export function selectVideoById(state: RootState, videoId: number) {
    const viewData = selectActiveViewData(state);
    const videosOrm = new PlainJsStoreORM(viewData[VIDEOS_KEY]);
    return videosOrm.getById(videoId);
}

export function selectVideoByIdInView(state: RootState, videoId: number, viewId: number) {
    const viewData = selectViewData(state, viewId);
    if (!viewData) throw new Error(`View with id ${viewId} not found`);
    const videosOrm = new PlainJsStoreORM(viewData[VIDEOS_KEY]);
    return videosOrm.getById(videoId);
}

export function selectNumVideos(state: RootState): number {
    const viewData = selectActiveViewData(state);
    const videosOrm = new PlainJsStoreORM(viewData[VIDEOS_KEY]);
    return videosOrm.length;
}

export function selectLoadedUpToPage(state: RootState): number {
    const viewData = selectActiveViewData(state);
    return viewData.loadedUpToPage;
}

export function selectFilterVideosByUserId(state: RootState): number | undefined {
    const viewData = selectActiveViewData(state);
    return viewData.filterByUserId;
}

export function getVideoSharePageUrl(state: RootState, videoOrmId: number): string {
    const video = selectVideoById(state, videoOrmId);
    if (!video) throw new Error(`Video with id ${videoOrmId} not found`);
    return video.page_url;
}

export function getMoreVideosToLoad(state: RootState): boolean {
    const viewData = selectActiveViewData(state);
    return viewData.moreVideosToLoad;
}

export function getVideosSortBy(state: RootState): VideoSortBy {
    const viewData = selectActiveViewData(state);
    return viewData.videosSortBy;
}

export function noProjects(state: RootState): boolean {
    const viewIds = selectViewIds(state);

    // Return true if there are no projects in the active workspace
    return (
        viewIds.filter((viewId) => {
            return (
                viewIdIsProject(viewId) &&
                selectViewData(state, viewId)?.workspaceId === selectActiveWorkspace(state)?.id
            );
        }).length <= 0
    );
}

export function getVideoSearchTerm(state: RootState): string {
    if (hasDashboardState(state)) {
        return state.dashboard.videoSearchTerm;
    } else {
        return '';
    }
}

export function selectRenderEstimatedStatus(
    state: RootState,
    videoId: number,
): EstimatedStatusEnum | undefined {
    const orm = new PlainJsStoreORM(state.dashboard.estimatedRenderDurations);
    return orm.getById(videoId)?.estimated_status;
}

export function selectRenderEstimatedTimeRemaining(
    state: RootState,
    videoId: number,
): number | undefined {
    const orm = new PlainJsStoreORM(state.dashboard.estimatedRenderDurations);
    return orm.getById(videoId)?.estimated_time_remaining;
}

export function hasDashboardState(state: RootState): boolean {
    return Object.prototype.hasOwnProperty.call(state, 'dashboard');
}

export function canCreateVideos(state: RootState) {
    if (SERVER.USER.role === RoleEnum.EDITOR && state.dashboard) {
        // Editors can only create videos if they are in the project
        const activeViewIsProject = selectActiveViewIsProject(state);
        return activeViewIsProject;
    }
    return roleCanCreateVideos(SERVER.USER.role);
}
